import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormGroup,
  Radio,
  RadioGroup,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { ReactElement } from 'react';
import { ChartNoteFont } from '../../../types/appointment';
import { DocumentState, GeneratedDocument } from '../../../types/document';
import { RichTextEditor } from '../components/RichTextEditor';
import { documentStyles as styles } from '../styles/documentPanelStyles';

interface DocumentFieldProps {
  fieldKey: string;
  fieldValue: string;
  fieldLabel: string;
  onChange: (key: string, value: unknown) => void;
  onBlur: () => void;
  scaleFontSize: (fontSize: string) => string;
  multiline?: boolean;
  rows?: number;
  isSelected?: boolean;
  onSelectionChange?: (fieldKey: string, isSelected: boolean) => void;
}

export function createDocumentField({
  fieldKey,
  fieldValue,
  fieldLabel,
  onChange,
  onBlur,
  scaleFontSize,
  multiline = true,
  rows,
  isSelected = true,
  onSelectionChange,
}: DocumentFieldProps): ReactElement {
  return (
    <Box key={fieldKey} sx={styles.fieldContainer}>
      <Box sx={styles.fieldLabelContainer}>
        <Typography variant='subtitle2' sx={styles.fieldLabelPrimary}>
          {fieldLabel}
        </Typography>
        {onSelectionChange && (
          <Checkbox
            size='medium'
            checked={isSelected}
            sx={styles.fieldCheckbox}
            onChange={(e) => onSelectionChange(fieldKey, e.target.checked)}
          />
        )}
      </Box>
      <TextField
        multiline={multiline}
        fullWidth
        value={fieldValue}
        onChange={(e) => onChange(fieldKey, e.target.value)}
        onBlur={onBlur}
        variant='outlined'
        size='small'
        rows={rows}
        inputProps={{
          'data-field-key': fieldKey,
        }}
        sx={styles.createDocumentFieldStyles(scaleFontSize)}
      />
    </Box>
  );
}

export function createStringContentField({
  fieldValue,
  onChange,
  onBlur,
  scaleFontSize,
}: Omit<DocumentFieldProps, 'fieldKey' | 'fieldLabel'>): ReactElement {
  return (
    <Box sx={styles.fieldContainer}>
      <Typography variant='subtitle2' sx={styles.fieldLabel}>
        Content
      </Typography>
      <TextField
        multiline
        fullWidth
        value={fieldValue}
        onChange={(e) => onChange('content', e.target.value)}
        onBlur={onBlur}
        variant='outlined'
        size='small'
        rows={10}
        inputProps={{
          'data-field-key': 'content',
        }}
        sx={styles.createStringContentFieldStyles(scaleFontSize)}
      />
    </Box>
  );
}

interface RichTextFieldProps {
  fieldKey: string;
  htmlValue: string;
  fieldLabel: string;
  onChange: (key: string, value: unknown) => void;
  onBlur: () => void;
  scaleFontSize: (fontSize: string) => string;
  isSelected?: boolean;
  onSelectionChange?: (fieldKey: string, isSelected: boolean) => void;
}

export function createRichTextField({
  fieldKey,
  htmlValue,
  fieldLabel,
  onChange,
  onBlur,
  scaleFontSize,
  isSelected = true,
  onSelectionChange,
}: RichTextFieldProps): ReactElement {
  return (
    <Box key={fieldKey} sx={styles.fieldContainer}>
      <Box sx={styles.fieldLabelContainer}>
        <Typography variant='subtitle2' sx={styles.fieldLabel}>
          {fieldLabel}
        </Typography>
        {onSelectionChange && (
          <Checkbox
            size='medium'
            checked={isSelected}
            sx={styles.fieldCheckbox}
            onChange={(e) => onSelectionChange(fieldKey, e.target.checked)}
          />
        )}
      </Box>
      <RichTextEditor
        value={htmlValue}
        onChange={(value) => onChange(fieldKey, value)}
        onBlur={onBlur}
        scaleFontSize={scaleFontSize}
      />
    </Box>
  );
}

export function createFallbackContent({
  content,
  scaleFontSize,
}: {
  content: unknown;
  scaleFontSize: (fontSize: string) => string;
}): ReactElement {
  return (
    <Typography
      variant='body2'
      sx={{
        lineHeight: 1.4,
        color: 'text.primary',
        fontSize: scaleFontSize('12px'),
      }}
    >
      {String(content)}
    </Typography>
  );
}

interface MultiSelectFieldProps {
  fieldKey: string;
  fieldValue: unknown;
  fieldLabel: string;
  options: string[];
  singleAnswer: boolean;
  minSelections?: number;
  maxSelections?: number;
  onChange: (key: string, value: unknown) => void;
  isSelected?: boolean;
  onSelectionChange?: (fieldKey: string, isSelected: boolean) => void;
}

export function normalizeMultiSelectValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === 'string');
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    return value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

export function createMultiSelectField({
  fieldKey,
  fieldValue,
  fieldLabel,
  options,
  singleAnswer,
  minSelections = 0,
  maxSelections,
  onChange,
  isSelected = true,
  onSelectionChange,
}: MultiSelectFieldProps): ReactElement {
  const selectedValues = normalizeMultiSelectValue(fieldValue);
  const hasReachedMaxSelections =
    typeof maxSelections === 'number' && selectedValues.length >= maxSelections;
  const hasReachedMinSelections = selectedValues.length <= minSelections;

  return (
    <Box key={fieldKey} sx={styles.fieldContainer}>
      <Box sx={styles.fieldLabelContainer}>
        <Typography variant='subtitle2' sx={styles.fieldLabelPrimary}>
          {fieldLabel}
        </Typography>
        {onSelectionChange && (
          <Checkbox
            size='medium'
            checked={isSelected}
            sx={styles.fieldCheckbox}
            onChange={(e) => onSelectionChange(fieldKey, e.target.checked)}
          />
        )}
      </Box>

      <FormControl component='fieldset' fullWidth>
        {singleAnswer ? (
          <RadioGroup
            value={selectedValues[0] ?? ''}
            onChange={(event) => {
              onChange(fieldKey, event.target.value);
            }}
          >
            {options.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio size='small' />}
                label={option}
              />
            ))}
          </RadioGroup>
        ) : (
          <FormGroup>
            {options.map((option) => {
              const checked = selectedValues.includes(option);
              const disableUncheckedOption =
                !checked && hasReachedMaxSelections;
              const disableCheckedOption =
                checked && minSelections > 0 && hasReachedMinSelections;
              return (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      size='small'
                      checked={checked}
                      disabled={disableUncheckedOption || disableCheckedOption}
                      onChange={(event) => {
                        const nextSelections = event.target.checked
                          ? [...selectedValues, option]
                          : selectedValues.filter((value) => value !== option);
                        onChange(fieldKey, nextSelections);
                      }}
                    />
                  }
                  label={option}
                />
              );
            })}
          </FormGroup>
        )}
        {!singleAnswer &&
          (typeof maxSelections === 'number' || minSelections > 0) && (
            <FormHelperText>
              {typeof maxSelections === 'number' && minSelections > 0
                ? `Select between ${minSelections} and ${maxSelections} options.`
                : typeof maxSelections === 'number'
                  ? `Select up to ${maxSelections} options.`
                  : `Select at least ${minSelections} option${minSelections === 1 ? '' : 's'}.`}
            </FormHelperText>
          )}
      </FormControl>
    </Box>
  );
}

export const renderSkeletonLines = () => {
  return (
    <>
      <Skeleton variant='text' width='100%' height={16} />
      <Skeleton variant='text' width='95%' height={16} />
      <Skeleton variant='text' width='90%' height={16} />
      <Skeleton variant='text' width='85%' height={16} />
      <Skeleton variant='text' width='92%' height={16} />
      <Skeleton variant='text' width='90%' height={16} />
      <Skeleton variant='text' width='85%' height={16} />
      <Skeleton variant='text' width='92%' height={16} />
      <Skeleton variant='text' width='92%' height={16} />
      <Skeleton variant='text' width='90%' height={16} />
      <Skeleton variant='text' width='85%' height={16} />
      <Skeleton variant='text' width='92%' height={16} />
      <Skeleton variant='text' width='85%' height={16} />
      <Skeleton variant='text' width='92%' height={16} />
      <Box>
        <Skeleton variant='text' width='100%' height={16} />
        <Skeleton variant='text' width='88%' height={16} />
        <Skeleton variant='text' width='95%' height={16} />
        <Skeleton variant='text' width='88%' height={16} />
        <Skeleton variant='text' width='95%' height={16} />
        <Skeleton variant='text' width='88%' height={16} />
        <Skeleton variant='text' width='95%' height={16} />
        <Skeleton variant='text' width='88%' height={16} />
        <Skeleton variant='text' width='95%' height={16} />
        <Skeleton variant='text' width='88%' height={16} />
        <Skeleton variant='text' width='95%' height={16} />
      </Box>
    </>
  );
};

export const documentContentUtils = {
  formatFieldLabel(key: string): string {
    const spaced = key.replace(/([A-Z])/g, ' $1').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
  },

  getFieldValue(
    documentState: DocumentState | null,
    key: string,
    fallbackValue: unknown
  ): unknown {
    return documentState?.content[key] ?? fallbackValue ?? '';
  },
};

const stripBlankHtml = (value: string): string =>
  value
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .trim();

export const hasRenderableDocumentContent = (
  documentState: DocumentState | null
): boolean =>
  Boolean(
    documentState?.content &&
      Object.values(documentState.content).some((value) => {
        const normalizedValue =
          typeof value === 'string'
            ? value
            : value == null
              ? ''
              : JSON.stringify(value);

        return stripBlankHtml(normalizedValue).length > 0;
      })
  );

const normalizeFontSize = (fontSize: string): string =>
  /^\d+$/.test(fontSize) ? `${fontSize}px` : fontSize;

const applyChartNoteFontToEnrichmentHtml = (
  html: string,
  chartNoteFont?: ChartNoteFont
): string => {
  if (
    !chartNoteFont ||
    !html.includes('data-enrichment="transcript"') ||
    typeof globalThis.document === 'undefined'
  ) {
    return html;
  }

  const container = globalThis.document.createElement('div');
  container.innerHTML = html;

  const enrichmentParagraphs = container.querySelectorAll(
    'p[data-enrichment="transcript"]'
  );

  enrichmentParagraphs.forEach((paragraph) => {
    const element = paragraph as HTMLElement;

    if (element.style.fontFamily || element.style.fontSize) {
      return;
    }

    const hasFontStyledDescendant = element.querySelector(
      '[style*="font-family"], [style*="font-size"]'
    );

    if (hasFontStyledDescendant) {
      return;
    }

    const span = globalThis.document.createElement('span');
    span.style.fontFamily = chartNoteFont.fontFamily;
    span.style.fontSize = normalizeFontSize(chartNoteFont.fontSize);

    while (element.firstChild) {
      span.appendChild(element.firstChild);
    }

    element.appendChild(span);
  });

  return container.innerHTML;
};

export const createDocumentState = (
  document: GeneratedDocument,
  chartNoteFont?: ChartNoteFont
): DocumentState => {
  let parsedContent: Record<string, unknown> = {};

  try {
    const parsed = JSON.parse(document.content as string);
    parsedContent = Object.fromEntries(
      Object.entries(parsed).map(([key, value]) => {
        return [
          key,
          typeof value === 'string'
            ? applyChartNoteFontToEnrichmentHtml(value, chartNoteFont)
            : value,
        ];
      })
    );
  } catch {
    const fallbackContent =
      typeof document.content === 'string'
        ? document.content
        : document.content == null
          ? ''
          : JSON.stringify(document.content);

    parsedContent = {
      content: applyChartNoteFontToEnrichmentHtml(
        fallbackContent,
        chartNoteFont
      ),
    };
  }

  return {
    id: document.id,
    content: parsedContent,
    isModified: false,
    templateId: document.templateId || '',
    templateName: document.templateName || '',
  };
};
